import i18n from 'i18next';

// Define the supported locales
export const SUPPORTED_LOCALES = ['en', 'ru', 'kk', 'ar', 'he'];

// RTL languages
export const RTL_LANGUAGES = ['ar', 'he'];

// Check if a language is RTL
export const isRtl = (language: string): boolean => {
  return RTL_LANGUAGES.includes(language);
};

/**
 * Extended options interface for date formatting
 */
export interface DateFormatOptions extends Intl.DateTimeFormatOptions {
  /**
   * Whether to use relative formatting (e.g., "2 hours ago")
   */
  relative?: boolean;
  
  /**
   * Whether to include time in the formatted output
   */
  includeTime?: boolean;
}

/**
 * Format a date according to the current locale
 * @param date The date to format
 * @param options Formatting options
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string | number,
  options: DateFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }
): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
    
  // Use relative formatting if requested
  if (options.relative) {
    return formatRelativeTime(dateObj);
  }
  
  // Include time if requested
  if (options.includeTime) {
    return formatDateTime(dateObj, options);
  }
  
  const locale = i18n.language || 'en';
  
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

/**
 * Format a time according to the current locale
 * @param date The date/time to format
 * @param options Intl.DateTimeFormat options
 * @returns Formatted time string
 */
export const formatTime = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = { 
    hour: 'numeric', 
    minute: 'numeric' 
  }
): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  const locale = i18n.language || 'en';
  
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

/**
 * Format a date and time according to the current locale
 * @param date The date/time to format
 * @param options Intl.DateTimeFormat options
 * @returns Formatted date and time string
 */
export const formatDateTime = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: 'numeric', 
    minute: 'numeric'
  }
): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  const locale = i18n.language || 'en';
  
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

/**
 * Format a relative time (e.g., "2 hours ago")
 * @param date Date to format relative to now
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date | string | number): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' || typeof date === 'number' 
    ? new Date(date) 
    : date;
  
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  
  // Handle future dates
  if (diffMs < 0) {
    return formatDateTime(dateObj);
  }
  
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  
  // Fall back to standard date format for older dates
  return formatDate(date);
};

/**
 * Get a date for a specific time range (today, yesterday, last week, etc.)
 * @param range Predefined time range
 * @returns Date object
 */
export const getDateForRange = (range: 'today' | 'yesterday' | 'week' | 'month' | 'year'): Date => {
  const date = new Date();
  
  switch (range) {
    case 'today':
      return date;
    case 'yesterday':
      date.setDate(date.getDate() - 1);
      return date;
    case 'week':
      date.setDate(date.getDate() - 7);
      return date;
    case 'month':
      date.setMonth(date.getMonth() - 1);
      return date;
    case 'year':
      date.setFullYear(date.getFullYear() - 1);
      return date;
    default:
      return date;
  }
};

/**
 * Format a number according to the current locale
 * @param num The number to format
 * @param options Intl.NumberFormat options
 * @returns Formatted number string
 */
export const formatNumber = (
  num: number,
  options: Intl.NumberFormatOptions = {}
): string => {
  const locale = i18n.language || 'en';
  
  return new Intl.NumberFormat(locale, options).format(num);
};

/**
 * Format a currency value according to the current locale
 * @param amount The amount to format
 * @param currency The currency code (default: USD)
 * @param options Intl.NumberFormat options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'USD',
  options: Intl.NumberFormatOptions = {}
): string => {
  const locale = i18n.language || 'en';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...options
  }).format(amount);
};

/**
 * Format a percentage according to the current locale
 * @param value The value to format as percentage
 * @param options Intl.NumberFormat options
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  options: Intl.NumberFormatOptions = {}
): string => {
  const locale = i18n.language || 'en';
  
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    ...options
  }).format(value / 100);
};

/**
 * Get the locale-specific date and time format settings
 * @returns Locale-specific format settings
 */
export const getLocaleSettings = (): {
  firstDayOfWeek: number;
  timeFormat: '12h' | '24h';
  dateFormat: string;
} => {
  const locale = i18n.language || 'en';
  
  // Default settings
  const settings = {
    firstDayOfWeek: 0, // Sunday
    timeFormat: '12h' as '12h' | '24h',
    dateFormat: 'MM/DD/YYYY'
  };
  
  // Locale-specific settings
  switch (locale) {
    case 'en':
      // US English uses Sunday as first day of week, 12-hour time, MM/DD/YYYY
      settings.firstDayOfWeek = 0;
      settings.timeFormat = '12h';
      settings.dateFormat = 'MM/DD/YYYY';
      break;
    case 'ru':
    case 'kk':
      // Russian and Kazakh use Monday as first day of week, 24-hour time, DD.MM.YYYY
      settings.firstDayOfWeek = 1;
      settings.timeFormat = '24h';
      settings.dateFormat = 'DD.MM.YYYY';
      break;
    case 'ar':
      // Arabic uses Saturday as first day of week, 24-hour time, DD/MM/YYYY
      settings.firstDayOfWeek = 6;
      settings.timeFormat = '24h';
      settings.dateFormat = 'DD/MM/YYYY';
      break;
    case 'he':
      // Hebrew uses Sunday as first day of week, 24-hour time, DD/MM/YYYY
      settings.firstDayOfWeek = 0;
      settings.timeFormat = '24h';
      settings.dateFormat = 'DD/MM/YYYY';
      break;
    default:
      // Default to US English settings
      break;
  }
  
  return settings;
}; 